import React from 'react';
import { Rocket, Megaphone, CalendarDays, Check, Award, BadgeCheck } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { useLanguage } from '../i18n';

const campaignIcons = [Rocket, Megaphone, CalendarDays];

const Portfolio = () => {
  const { t } = useLanguage();

  return (
    <section id="portfolio" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#E9782A] font-semibold text-sm uppercase tracking-wider">
            {t.portfolio.label}
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
            {t.portfolio.titlePre}{' '}
            <span className="text-[#E9782A]">{t.portfolio.titleHighlight}</span>
          </h2>
          <p className="text-xl text-gray-600">
            {t.portfolio.subtitle}
          </p>
        </div>

        {/* Featured Client Campaign */}
        <Card className="mb-12 border-2 border-[#E9782A]/30 bg-gradient-to-br from-white to-orange-50/50 shadow-xl overflow-hidden" data-testid="featured-campaign">
          <div className="h-2 bg-gradient-to-r from-[#E9782A] to-[#d96820]"></div>
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-shrink-0 flex items-center justify-center w-20 h-20 rounded-2xl bg-[#E9782A]/10 text-5xl">
                {t.portfolio.featured.flag}
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <Badge className="bg-[#E9782A] text-white hover:bg-[#d96820]">
                    <BadgeCheck size={14} className="mr-1" /> {t.portfolio.featuredLabel}
                  </Badge>
                  <Badge variant="outline" className="border-[#E9782A] text-[#E9782A]">
                    {t.portfolio.featured.country}
                  </Badge>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                  {t.portfolio.featured.client}
                  <span className="text-gray-500 font-normal text-base ml-2">— {t.portfolio.featured.role}</span>
                </h3>
                <h4 className="font-semibold text-[#E9782A] mb-2">{t.portfolio.featured.campaign}</h4>
                <p className="text-gray-600 leading-relaxed mb-4">{t.portfolio.featured.description}</p>
                <div className="flex flex-wrap gap-6 pt-4 border-t border-[#E9782A]/10" data-testid="featured-campaign-stats">
                  {t.portfolio.featured.stats.map((stat) => (
                    <div key={stat.label}>
                      <div className="text-2xl font-bold text-[#E9782A]">{stat.value}</div>
                      <div className="text-xs text-gray-500 font-medium">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <Award className="text-[#E9782A] flex-shrink-0 hidden md:block" size={40} />
            </div>
          </CardContent>
        </Card>

        {/* Campaign Types Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {t.portfolio.campaigns.map((campaign, index) => {
            const Icon = campaignIcons[index];
            return (
              <Card
                key={campaign.title}
                className="group hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-[#E9782A]/20 overflow-hidden bg-white animate-in fade-in slide-in-from-bottom"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-xl bg-[#E9782A]/10 flex items-center justify-center mb-6 group-hover:bg-[#E9782A] transition-colors duration-300">
                    <Icon className="text-[#E9782A] group-hover:text-white transition-colors duration-300" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#E9782A] transition-colors duration-300">
                    {campaign.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-5">{campaign.description}</p>
                  <ul className="space-y-3 pt-4 border-t border-gray-100">
                    {campaign.points.map((point) => (
                      <li key={point} className="flex items-start space-x-2 text-sm text-gray-700">
                        <Check size={16} className="text-[#E9782A] mt-0.5 flex-shrink-0" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Image Section */}
        <div className="grid md:grid-cols-2 gap-8 mt-16">
          <div className="relative rounded-2xl overflow-hidden shadow-xl group">
            <img
              src="https://images.unsplash.com/photo-1676276375742-9e3d10e39d45"
              alt="Strategy planning"
              className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#E9782A]/80 to-transparent flex items-end p-8">
              <div className="text-white">
                <h3 className="text-2xl font-bold mb-2">{t.portfolio.img1Title}</h3>
                <p className="text-sm opacity-90">{t.portfolio.img1Text}</p>
              </div>
            </div>
          </div>

          <div className="relative rounded-2xl overflow-hidden shadow-xl group">
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71"
              alt="Analytics"
              className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#E9782A]/80 to-transparent flex items-end p-8">
              <div className="text-white">
                <h3 className="text-2xl font-bold mb-2">{t.portfolio.img2Title}</h3>
                <p className="text-sm opacity-90">{t.portfolio.img2Text}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
